// Communications page for Sunlight Grounds Care Client Portal
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '@/components/layout/Header';
import api from '@/api/client';
import { useProperty } from '@/hooks/useProperty';
import { Send, ChevronDown, X } from 'lucide-react';

export default function Comms({ user }) {
  const { selectedProperty } = useProperty();
  const location = useLocation();
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  
  // Request modal state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [activeChip, setActiveChip] = useState(null);
  const [requestTypes, setRequestTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Request form state
  const [selectedType, setSelectedType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [preferredDate, setPreferredDate] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!selectedProperty) {
        setLoading(false);
        return;
      }
      
      try {
        const results = await Promise.allSettled([
          api.getMessages(),
          api.getServiceRequestTypes(selectedProperty.id),
          api.getLocations(selectedProperty.id)
        ]);
        
        if (results[0].status === 'fulfilled') setMessages(results[0].value || []);
        if (results[1].status === 'fulfilled') {
          const types = results[1].value;
          setRequestTypes(Array.isArray(types) ? types : []);
        }
        if (results[2].status === 'fulfilled') setLocations(results[2].value || {});
      } catch (err) {
        console.error('Failed to load comms data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProperty]);

  // Handle navigation state (from quick actions)
  useEffect(() => {
    if (location.state?.activateRequest) {
      setActiveChip('request');
      setShowRequestModal(true);
    }
    if (location.state?.focusInput) {
      inputRef.current?.focus();
    }
    if (location.state?.prefillType) {
      setSelectedType(location.state.prefillType);
    }
  }, [location.state]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!messageText.trim() || sending) return;
    
    setSending(true);
    const text = messageText;
    setMessageText('');
    
    try {
      const newMsg = await api.sendMessage(text);
      setMessages(prev => [...prev, newMsg]);
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessageText(text); // Restore on error
    } finally {
      setSending(false);
    }
  };

  const handleChipClick = (chipId) => {
    if (activeChip === chipId) {
      setActiveChip(null);
      setShowRequestModal(false);
    } else {
      setActiveChip(chipId);
      if (chipId === 'request') {
        setShowRequestModal(true);
      } else {
        setShowRequestModal(false);
        // Pre-fill message for other chips
        if (chipId === 'reschedule') {
          setMessageText('I need to reschedule my upcoming visit. ');
          inputRef.current?.focus();
        } else if (chipId === 'feedback') {
          setMessageText('Feedback on my recent service: ');
          inputRef.current?.focus();
        } else if (chipId === 'question') {
          setMessageText('Quick question: ');
          inputRef.current?.focus();
        }
      }
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!selectedType || !selectedLocation || !selectedProperty) return;
    
    setSending(true);
    
    try {
      const request = await api.createServiceRequest({
        property_id: selectedProperty.id,
        request_type: requestTypes.find(t => t.id === selectedType)?.name || selectedType,
        service_type: selectedType,
        location_text: selectedLocation,
        description: `Preferred date: ${preferredDate || 'Flexible'}`
      });
      
      // Add request card to thread
      const requestCard = {
        id: `req-${Date.now()}`,
        type: 'request',
        request_type: requestTypes.find(t => t.id === selectedType)?.name || selectedType,
        location: selectedLocation,
        date: preferredDate,
        status: 'pending',
        time: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
      };
      
      setMessages(prev => [...prev, requestCard]);
      
      // Close modal and reset
      setShowRequestModal(false);
      setActiveChip(null);
      setSelectedType('');
      setSelectedLocation('');
      
      // Add team reply after delay
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: `msg-${Date.now()}`,
          from: 'team',
          text: "Got your request! We'll review it and get back to you within 24 hours.",
          time: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
        }]);
      }, 800);
      
    } catch (err) {
      console.error('Failed to submit request:', err);
    } finally {
      setSending(false);
    }
  };

  const allLocations = [
    ...(locations.zones || []),
    ...(locations.rooms || [])
  ].map(loc => ({ ...loc, name: loc.display_name || loc.name || loc.room_number }));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-light">
        <Header title="Messages" property={selectedProperty} />
        <div className="p-4">
          <div className="sg-card p-4 animate-pulse">
            <div className="h-20 bg-slate/20 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-light" data-testid="comms-page">
      <Header title="Messages" property={selectedProperty} />
      
      {/* Message Thread */}
      <div 
        ref={threadRef}
        className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 py-3 space-y-3 max-w-4xl mx-auto w-full"
        data-testid="message-thread"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-14 h-14 rounded-full bg-cream flex items-center justify-center mb-3">
              <Send size={22} className="text-slate/40 -rotate-45" />
            </div>
            <p className="font-dm text-sm font-medium text-navy">No messages yet</p>
            <p className="font-dm text-[11px] text-slate mt-1 max-w-[240px]">
              Send a message or submit a service request using the options below
            </p>
          </div>
        ) : (
          messages.map((msg) => (
          msg.type === 'request' ? (
            // Request Card
            <div 
              key={msg.id}
              className="ml-auto w-[85%] bg-cream border border-gold-light rounded-xl rounded-br-sm overflow-hidden"
              data-testid="request-card"
            >
              <div className="bg-gold-pale px-3 py-2 border-b border-gold-light flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>📋</span>
                  <span className="font-dm text-[10px] font-bold text-[#996B00]">New service request</span>
                </div>
                <span className="text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-gold-light text-[#996B00]">
                  {msg.status}
                </span>
              </div>
              <div className="p-3">
                <p className="font-dm text-[11px] font-bold text-navy">{msg.request_type}</p>
                <p className="font-dm text-[10px] text-slate mt-0.5">
                  {msg.location} · {msg.date || 'Flexible date'}
                </p>
              </div>
            </div>
          ) : (
            // Regular Message Bubble
            <div key={msg.id} className={`max-w-[85%] ${msg.from === 'client' ? 'ml-auto' : ''}`}>
              <p className="text-[9px] font-dm text-slate mb-1">{msg.time}</p>
              <div 
                className={`
                  px-3 py-2.5 rounded-xl font-dm text-[11px] leading-relaxed
                  ${msg.from === 'client' 
                    ? 'bg-gold-pale text-[#5a4010] rounded-br-sm' 
                    : 'bg-green-light text-navy rounded-bl-sm'
                  }
                `}
                data-testid={`message-${msg.from}`}
              >
                {msg.text}
              </div>
            </div>
          )
        ))
        )}
      </div>

      {/* Request Modal */}
      <div 
        className={`
          bg-slate-light border-t border-black/[0.08] overflow-hidden transition-all duration-300
          ${showRequestModal ? 'max-h-72' : 'max-h-0'}
        `}
        data-testid="request-modal"
      >
        <form onSubmit={handleSubmitRequest} className="p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-dm text-xs font-bold text-navy">New Service Request</h3>
            <button 
              type="button"
              onClick={() => { setShowRequestModal(false); setActiveChip(null); }}
              className="text-slate hover:text-navy"
            >
              <X size={16} />
            </button>
          </div>
          
          {/* Service Type */}
          <div className="relative">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-black/[0.08] bg-cream
                         font-dm text-[11px] text-navy appearance-none cursor-pointer
                         focus:outline-none focus:border-navy/30"
              required
              data-testid="service-type-select"
            >
              <option value="">Select service type</option>
              {requestTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
          </div>
          
          {/* Area */}
          <div className="relative">
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-black/[0.08] bg-cream
                         font-dm text-[11px] text-navy appearance-none cursor-pointer
                         focus:outline-none focus:border-navy/30"
              required
              data-testid="location-select"
            >
              <option value="">Select area</option>
              {allLocations.map(loc => (
                <option key={loc.id} value={loc.name}>{loc.name}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
          </div>
          
          {/* Preferred Date */}
          <input
            type="date"
            value={preferredDate}
            onChange={(e) => setPreferredDate(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-black/[0.08] bg-cream
                       font-dm text-[11px] text-navy
                       focus:outline-none focus:border-navy/30"
            data-testid="preferred-date-input"
          />
          
          <button
            type="submit"
            disabled={sending || !selectedType || !selectedLocation}
            className="w-full bg-navy text-white font-dm text-[11px] font-medium py-2.5 rounded-xl
                       hover:bg-navy-light transition-colors disabled:opacity-60"
            data-testid="submit-request-btn"
          >
            {sending ? 'Sending...' : 'Send request'}
          </button>
        </form>
      </div>

      {/* Request Chips Row */}
      <div className="bg-cream border-t border-black/[0.08] px-4 py-2 overflow-x-auto scrollbar-hide" data-testid="request-chips">
        <div className="flex gap-2">
          {[
            { id: 'request', icon: '📋', label: 'Request service' },
            { id: 'reschedule', icon: '📅', label: 'Reschedule' },
            { id: 'feedback', icon: '💬', label: 'Feedback' },
            { id: 'question', icon: '❓', label: 'Question' }
          ].map(chip => (
            <button
              key={chip.id}
              onClick={() => handleChipClick(chip.id)}
              className={`
                flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border
                font-dm text-[10px] font-bold transition-colors
                ${activeChip === chip.id 
                  ? 'bg-navy text-white border-navy' 
                  : 'bg-cream text-navy border-black/[0.12] hover:border-navy/30'
                }
              `}
              data-testid={`chip-${chip.id}`}
            >
              <span>{chip.icon}</span>
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {/* Compose Bar */}
      <div className="bg-cream px-4 sm:px-6 md:px-8 py-2.5 pb-20 flex items-center gap-2 max-w-4xl mx-auto" data-testid="compose-bar">
        <input
          ref={inputRef}
          type="text"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Type a message..."
          className="flex-1 px-4 py-2 rounded-full border border-black/[0.08] bg-slate-light
                     font-dm text-[11px] text-navy placeholder:text-slate/50
                     focus:outline-none focus:border-navy/30"
          data-testid="message-input"
        />
        <button
          onClick={handleSendMessage}
          disabled={!messageText.trim() || sending}
          className="w-8 h-8 rounded-full bg-navy flex items-center justify-center
                     hover:bg-navy-light transition-colors disabled:opacity-60"
          data-testid="send-message-btn"
        >
          <Send size={14} className="text-white -rotate-45" />
        </button>
      </div>
    </div>
  );
}
